import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Paper,
  Autocomplete
} from '@mui/material';
import { 
  Save, 
  Inventory, 
  TrendingUp,
  Code,
  NotificationImportant,
  EventAvailable,
  Business,
  Lock,
  AttachMoney,
  ShoppingCart,
  Visibility,
  MoneyOff
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import config from '../../../config';
import axios from 'axios';
import ItemSupplierManagement from './ItemSupplierManagement';
import AccountSelector from '../../../components/common/AccountSelector';

const translations = {
  ar: {
    title: 'إعدادات المخزون والمشتريات',
    subtitle: 'تخصيص طرق التكلفة وقواعد إدارة المخزون والموردين',
    costingMethod: 'طريقة التكلفة',
    codeSettings: 'إعدادات الكود',
    alerts: 'التنبيهات',
    itemSupplierAssignment: 'تخصيص الأصناف والموردين',
    itemSupplierAssignmentDesc: 'إعدادات ربط الأصناف بالموردين والتحكم في الأسعار',
    purchaseSettings: 'إعدادات المشتريات',
    lossOnReturnsAccount: 'حساب خسارة/ربح مرتجع المشتريات',
    lossAccountDesc: 'الحساب المستخدم لتسجيل فرق التكلفة في مرتجعات المشتريات',
    
    fifo: 'الوارد أولاً صادر أولاً (FIFO)',
    lifo: 'الوارد أخيراً صادر أولاً (LIFO)',
    weighted: 'المتوسط المرجح',
    
    allowNegativeStock: 'السماح بمخزون سالب',
    autoGenerateCode: 'توليد أكواد تلقائي',
    codePrefix: 'بادئة الكود',
    minStockAlert: 'تنبيه الحد الأدنى',
    trackExpiry: 'تتبع تاريخ الانتهاء',
    
    enableSupplierAssignment: 'تفعيل نظام ربط الأصناف بالموردين',
    requireSupplierForItems: 'مورد إجباري للأصناف',
    enablePriceControl: 'تفعيل التحكم في الأسعار من المورد',
    allowMultipleSuppliersPerItem: 'السماح بعدة موردين لكل صنف',
    autoUpdatePricesFromSupplier: 'تحديث الأسعار تلقائياً',
    lockPriceWhenSupplierSet: 'قفل السعر عند تحديد المورد',
    showSupplierPolicyChips: 'إظهار علامات سياسة المورد في الواجهة',
    
    requireApprovalForPO: 'اعتماد إجباري لأوامر الشراء',
    allowPartialReceiving: 'السماح بالاستلام الجزئي',
    trackPurchaseReturns: 'تتبع مرتجعات المشتريات',
    
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات',
    loadError: 'فشل تحميل الإعدادات',
    
    featureEnabled: 'الميزة مفعّلة',
    featureDisabled: 'الميزة معطّلة',
    
    supplierPolicy: 'سياسة المورد',
    priceControl: 'التحكم في الأسعار',
    priceUpdates: 'تحديث الأسعار',
    uiDisplay: 'العرض في الواجهة',
    
    infoMessage: 'عند تفعيل هذا النظام:',
    infoPoint1: 'سيظهر في طلب الشراء والفاتورة: "مورد إجباري" و "سعر مقفول"',
    infoPoint2: 'سيتم قفل حقل السعر تلقائياً عند تحديد المورد',
    infoPoint3: 'يمكن تخصيص عدة موردين لكل صنف مع أسعار مختلفة',
    infoPoint4: 'سيتم منع إضافة أصناف بدون مورد إذا كان المورد إجباري',
  },
  en: {
    title: 'Inventory & Purchasing Settings',
    subtitle: 'Customize costing methods, inventory rules and supplier management',
    costingMethod: 'Costing Method',
    codeSettings: 'Code Settings',
    alerts: 'Alerts',
    itemSupplierAssignment: 'Item-Supplier Assignment',
    itemSupplierAssignmentDesc: 'Settings for linking items with suppliers and price control',
    purchaseSettings: 'Purchase Settings',
    lossOnReturnsAccount: 'Loss/Gain on Purchase Returns Account',
    lossAccountDesc: 'Account used to record cost differences in purchase returns',
    
    fifo: 'First In First Out (FIFO)',
    lifo: 'Last In First Out (LIFO)',
    weighted: 'Weighted Average',
    
    allowNegativeStock: 'Allow Negative Stock',
    autoGenerateCode: 'Auto Generate Code',
    codePrefix: 'Code Prefix',
    minStockAlert: 'Min Stock Alert',
    trackExpiry: 'Track Expiry Date',
    
    enableSupplierAssignment: 'Enable Item-Supplier Assignment System',
    requireSupplierForItems: 'Supplier Required for Items',
    enablePriceControl: 'Enable Price Control from Supplier',
    allowMultipleSuppliersPerItem: 'Allow Multiple Suppliers per Item',
    autoUpdatePricesFromSupplier: 'Auto Update Prices from Supplier',
    lockPriceWhenSupplierSet: 'Lock Price when Supplier is Set',
    showSupplierPolicyChips: 'Show Supplier Policy Chips in UI',
    
    requireApprovalForPO: 'Require Approval for Purchase Orders',
    allowPartialReceiving: 'Allow Partial Receiving',
    trackPurchaseReturns: 'Track Purchase Returns',
    
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    loadError: 'Failed to load settings',
    
    featureEnabled: 'Feature Enabled',
    featureDisabled: 'Feature Disabled',
    
    supplierPolicy: 'Supplier Policy',
    priceControl: 'Price Control',
    priceUpdates: 'Price Updates',
    uiDisplay: 'UI Display',
    
    infoMessage: 'When this system is enabled:',
    infoPoint1: 'Purchase Order and Invoice will show: "Supplier Required" and "Price Locked"',
    infoPoint2: 'Price field will be automatically locked when supplier is selected',
    infoPoint3: 'Multiple suppliers can be assigned to each item with different prices',
    infoPoint4: 'Items without suppliers will be blocked if supplier is required',
  }
};

export default function InventorySettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    // Inventory Settings — keys must match DB exactly
    DefaultCostingMethod: 'FIFO',
    AllowNegativeStock: false,
    autoGenerateCode: true,
    codePrefix: 'ITM',
    LowStockAlertEnabled: true,
    trackExpiry: false,
    
    // Item-Supplier Assignment Settings
    enableSupplierAssignment: true,
    requireSupplierForItems: false,
    enablePriceControl: true,
    allowMultipleSuppliersPerItem: true,
    autoUpdatePricesFromSupplier: false,
    lockPriceWhenSupplierSet: true,
    showSupplierPolicyChips: true,
    
    // Purchase Settings
    requireApprovalForPO: false,
    allowPartialReceiving: true,
    trackPurchaseReturns: true,
    
    // ✅ NEW: Loss on Returns Account (capital L to match backend)
    LossOnReturnsAccountId: null as number | null,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to get settings from API
      const response = await axios.get(`${config.API_BASE_URL}/SystemSettings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      // Handle different response structures
      let settingsData = [];
      
      if (Array.isArray(response.data)) {
        settingsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        settingsData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Convert object to array
        settingsData = Object.entries(response.data).map(([key, value]) => ({
          settingKey: key,
          settingValue: value
        }));
      }
      
      // Map settings to formData
      const settings: Record<string, any> = {};
      
      settingsData.forEach((setting: any) => {
        const key = setting.settingKey ?? setting.key ?? setting.name;
        const value = setting.settingValue ?? setting.value;
        
        if (key) {
          // Convert string boolean to actual boolean
          if (value === 'true' || value === true) {
            settings[key] = true;
          } else if (value === 'false' || value === false) {
            settings[key] = false;
          } else if (key === 'LossOnReturnsAccountId') {
            // ✅ Handle Loss Account ID specially
            if (value && value !== '' && value !== 'null') {
              settings[key] = parseInt(value, 10);
            } else {
              settings[key] = null;
            }
          } else {
            settings[key] = value;
          }
        }
      });
      
      
      // Update formData with loaded settings
      setFormData(prev => ({ ...prev, ...settings }));
      
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      console.error('Error details:', error.response?.data);
      toast.warning(isRTL ? 'تم تحميل الإعدادات الافتراضية' : 'Default settings loaded');
      // Don't show error, just use default values
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const token = localStorage.getItem('token');
      
      // Convert to Dictionary<string, string> format
      const settingsDict: Record<string, string> = {};
      
      Object.entries(formData).forEach(([key, value]) => {
        // ✅ Handle null specially for LossOnReturnsAccountId
        if (key === 'LossOnReturnsAccountId') {
          settingsDict[key] = value != null ? value.toString() : '';
        } else {
          settingsDict[key] = value != null ? value.toString() : '';
        }
      });


      await axios.put(
        `${config.API_BASE_URL}/SystemSettings`, 
        settingsDict,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(t.saveSuccess);
      
      // Reload settings to confirm
      await loadSettings();
      
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Inventory sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack spacing={3}>
        {/* Costing Method Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <TrendingUp sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.costingMethod}
              </Typography>
            </Stack>

            <Autocomplete
              fullWidth
              options={[
                { value: 'FIFO', label: t.fifo },
                { value: 'LIFO', label: t.lifo },
                { value: 'WEIGHTED', label: t.weighted }
              ]}
              value={{ value: formData.DefaultCostingMethod, label: formData.DefaultCostingMethod === 'FIFO' ? t.fifo : formData.DefaultCostingMethod === 'LIFO' ? t.lifo : t.weighted }}
              onChange={(_, newValue) => {
                if (newValue) {
                  setFormData(prev => ({ ...prev, DefaultCostingMethod: newValue.value }));
                }
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.costingMethod}
                  size="medium"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              disableClearable
            />
          </CardContent>
        </Card>

        {/* Code Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(118, 75, 162, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Code sx={{ color: '#764ba2', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.codeSettings}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoGenerateCode}
                    onChange={handleChange}
                    name="autoGenerateCode"
                    color="secondary"
                  />
                }
                label={t.autoGenerateCode}
              />

              <TextField
                fullWidth
                label={t.codePrefix}
                name="codePrefix"
                value={formData.codePrefix}
                onChange={handleChange}
                disabled={!formData.autoGenerateCode}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ✅ NEW: Loss on Returns Account Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(239, 68, 68, 0.2)',
              transform: 'translateY(-4px)',
              borderColor: 'rgba(239, 68, 68, 0.5)',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(239, 68, 68, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <MoneyOff sx={{ color: '#ef4444', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600">
                  {t.lossOnReturnsAccount}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t.lossAccountDesc}
                </Typography>
              </Box>
            </Stack>

            <AccountSelector
              value={formData.LossOnReturnsAccountId}
              onChange={(accountId) => setFormData(prev => ({ ...prev, LossOnReturnsAccountId: accountId }))}
              label={t.lossOnReturnsAccount}
              onlyFinalAccounts={true}
              fullWidth
            />

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="caption" component="div" sx={{ mb: 1, fontWeight: 600 }}>
                {isRTL ? '💡 كيفية البحث:' : '💡 How to search:'}
              </Typography>
              <Typography variant="caption" component="div">
                {isRTL 
                  ? '• اضغط على الحقل واكتب كود الحساب أو اسمه (مثال: اكتب "510" أو "مصروفات")'
                  : '• Click the field and type account code or name (e.g., type "510" or "expense")'
                }
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                {isRTL 
                  ? 'يستخدم هذا الحساب عند وجود فرق بين سعر الشراء الأصلي (UnitPrice) والتكلفة الفعلية (UnitCost شاملة الشحن) في مرتجعات المشتريات'
                  : 'This account is used when there is a difference between the original purchase price (UnitPrice) and actual cost (UnitCost including freight) in purchase returns'
                }
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Item-Supplier Assignment Card - MAIN SECTION */}
        <Paper
          elevation={8}
          sx={{ 
            borderRadius: 3,
            border: '3px solid',
            borderColor: formData.enableSupplierAssignment ? '#fa709a' : 'rgba(0,0,0,0.12)',
            background: formData.enableSupplierAssignment 
              ? 'linear-gradient(135deg, rgba(250, 112, 154, 0.03) 0%, rgba(254, 225, 64, 0.03) 100%)'
              : 'transparent',
            transition: 'all 0.4s ease',
            boxShadow: formData.enableSupplierAssignment
              ? '0 8px 40px rgba(250, 112, 154, 0.25)'
              : '0 4px 20px rgba(0,0,0,0.08)',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: formData.enableSupplierAssignment
                ? '0 12px 50px rgba(250, 112, 154, 0.35)'
                : '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header with Gradient Background */}
            <Box 
              sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                borderRadius: 2,
                p: 3,
                mb: 3,
                color: 'white',
                boxShadow: '0 4px 20px rgba(250, 112, 154, 0.3)'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)', 
                    p: 1.5, 
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Business sx={{ fontSize: 32 }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight="700">
                    {t.itemSupplierAssignment}
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ opacity: 0.95, mt: 0.5 }}>
                    {t.itemSupplierAssignmentDesc}
                  </Typography>
                </Box>
                <Chip
                  label={formData.enableSupplierAssignment ? t.featureEnabled : t.featureDisabled}
                  sx={{ 
                    fontWeight: 700,
                    height: 38,
                    fontSize: '0.95rem',
                    bgcolor: formData.enableSupplierAssignment ? '#10b981' : '#64748b',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                />
              </Stack>
            </Box>

            {/* Main Enable/Disable Toggle */}
            <Box 
              sx={{ 
                bgcolor: formData.enableSupplierAssignment 
                  ? 'rgba(250, 112, 154, 0.08)' 
                  : 'rgba(0,0,0,0.02)',
                borderRadius: 2,
                p: 3,
                mb: 3,
                border: '2px solid',
                borderColor: formData.enableSupplierAssignment 
                  ? 'rgba(250, 112, 154, 0.3)' 
                  : 'transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableSupplierAssignment}
                    onChange={handleChange}
                    name="enableSupplierAssignment"
                    sx={{ 
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#fa709a',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#fa709a',
                      },
                    }}
                  />
                }
                label={
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Business sx={{ fontSize: 26, color: '#fa709a' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600" fontSize="1.1rem">
                        {t.enableSupplierAssignment}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize="0.85rem">
                        {formData.enableSupplierAssignment 
                          ? (isRTL ? 'النظام مفعّل - يمكن ربط الأصناف بالموردين والتحكم في الأسعار' : 'System enabled - Items can be linked to suppliers with price control')
                          : (isRTL ? 'النظام معطل - لن يتم ربط الأصناف بالموردين' : 'System disabled - Items will not be linked to suppliers')
                        }
                      </Typography>
                    </Box>
                  </Stack>
                }
              />
            </Box>

            {/* Settings Grid - 4 Colored Sections */}
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                gap: 2.5,
                opacity: formData.enableSupplierAssignment ? 1 : 0.4,
                pointerEvents: formData.enableSupplierAssignment ? 'auto' : 'none',
                transition: 'opacity 0.3s ease'
              }}
            >
              {/* 1. Supplier Policy - Blue */}
              <Paper
                elevation={2}
                sx={{ 
                  bgcolor: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: 2,
                  p: 2.5,
                  border: '2px solid rgba(59, 130, 246, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                    bgcolor: 'rgba(59, 130, 246, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <Business sx={{ color: '#3b82f6', fontSize: 22 }} />
                  <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" fontSize="0.95rem">
                    {t.supplierPolicy}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requireSupplierForItems}
                        onChange={handleChange}
                        name="requireSupplierForItems"
                        disabled={!formData.enableSupplierAssignment}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(59, 130, 246, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.requireSupplierForItems}</Typography>}
                  />
                  <Divider sx={{ my: 0.5 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allowMultipleSuppliersPerItem}
                        onChange={handleChange}
                        name="allowMultipleSuppliersPerItem"
                        disabled={!formData.enableSupplierAssignment}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(59, 130, 246, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.allowMultipleSuppliersPerItem}</Typography>}
                  />
                </Stack>
              </Paper>

              {/* 2. Price Control - Red */}
              <Paper
                elevation={2}
                sx={{ 
                  bgcolor: 'rgba(239, 68, 68, 0.05)',
                  borderRadius: 2,
                  p: 2.5,
                  border: '2px solid rgba(239, 68, 68, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(239, 68, 68, 0.4)',
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <Lock sx={{ color: '#ef4444', fontSize: 22 }} />
                  <Typography variant="subtitle2" fontWeight="700" color="#ef4444" fontSize="0.95rem">
                    {t.priceControl}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enablePriceControl}
                        onChange={handleChange}
                        name="enablePriceControl"
                        disabled={!formData.enableSupplierAssignment}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef4444' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(239, 68, 68, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.enablePriceControl}</Typography>}
                  />
                  <Divider sx={{ my: 0.5 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.lockPriceWhenSupplierSet}
                        onChange={handleChange}
                        name="lockPriceWhenSupplierSet"
                        disabled={!formData.enableSupplierAssignment || !formData.enablePriceControl}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef4444' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(239, 68, 68, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.lockPriceWhenSupplierSet}</Typography>}
                  />
                </Stack>
              </Paper>

              {/* 3. Price Updates - Green */}
              <Paper
                elevation={2}
                sx={{ 
                  bgcolor: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: 2,
                  p: 2.5,
                  border: '2px solid rgba(16, 185, 129, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                    bgcolor: 'rgba(16, 185, 129, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <AttachMoney sx={{ color: '#10b981', fontSize: 22 }} />
                  <Typography variant="subtitle2" fontWeight="700" color="#10b981" fontSize="0.95rem">
                    {t.priceUpdates}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.autoUpdatePricesFromSupplier}
                        onChange={handleChange}
                        name="autoUpdatePricesFromSupplier"
                        disabled={!formData.enableSupplierAssignment}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(16, 185, 129, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.autoUpdatePricesFromSupplier}</Typography>}
                  />
                </Stack>
              </Paper>

              {/* 4. UI Display - Purple */}
              <Paper
                elevation={2}
                sx={{ 
                  bgcolor: 'rgba(139, 92, 246, 0.05)',
                  borderRadius: 2,
                  p: 2.5,
                  border: '2px solid rgba(139, 92, 246, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(139, 92, 246, 0.4)',
                    bgcolor: 'rgba(139, 92, 246, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <Visibility sx={{ color: '#8b5cf6', fontSize: 22 }} />
                  <Typography variant="subtitle2" fontWeight="700" color="#8b5cf6" fontSize="0.95rem">
                    {t.uiDisplay}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showSupplierPolicyChips}
                        onChange={handleChange}
                        name="showSupplierPolicyChips"
                        disabled={!formData.enableSupplierAssignment}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#8b5cf6' },
                          '& .MuiSwitch-track': { bgcolor: 'rgba(139, 92, 246, 0.3)' }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t.showSupplierPolicyChips}</Typography>}
                  />
                </Stack>
              </Paper>
            </Box>

            {/* Info Alert */}
            {formData.enableSupplierAssignment && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 3, 
                  borderRadius: 2,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  '& .MuiAlert-icon': {
                    fontSize: 28
                  }
                }}
              >
                <Typography variant="body2" fontWeight="600" mb={1}>
                  💡 {t.infoMessage}
                </Typography>
                <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>
                  • {t.infoPoint1}<br/>
                  • {t.infoPoint2}<br/>
                  • {t.infoPoint3}<br/>
                  • {t.infoPoint4}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Paper>

        {/* Purchase Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(16, 185, 129, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
              transform: 'translateY(-4px)',
              borderColor: 'rgba(16, 185, 129, 0.5)',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(16, 185, 129, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <ShoppingCart sx={{ color: '#10b981', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.purchaseSettings}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requireApprovalForPO}
                    onChange={handleChange}
                    name="requireApprovalForPO"
                    color="success"
                  />
                }
                label={t.requireApprovalForPO}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowPartialReceiving}
                    onChange={handleChange}
                    name="allowPartialReceiving"
                    color="success"
                  />
                }
                label={t.allowPartialReceiving}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.trackPurchaseReturns}
                    onChange={handleChange}
                    name="trackPurchaseReturns"
                    color="success"
                  />
                }
                label={t.trackPurchaseReturns}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Alerts Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(239, 68, 68, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <NotificationImportant sx={{ color: '#ef4444', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.alerts}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.AllowNegativeStock}
                    onChange={handleChange}
                    name="AllowNegativeStock"
                  />
                }
                label={t.allowNegativeStock}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.LowStockAlertEnabled}
                    onChange={handleChange}
                    name="LowStockAlertEnabled"
                  />
                }
                label={t.minStockAlert}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.trackExpiry}
                    onChange={handleChange}
                    name="trackExpiry"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EventAvailable sx={{ fontSize: 20 }} />
                    <span>{t.trackExpiry}</span>
                  </Stack>
                }
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Item-Supplier Assignment Management Section */}
      <ItemSupplierManagement />

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, mb: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 6,
            py: 1.8,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontSize: '1.05rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #63418b 100%)',
              boxShadow: '0 6px 25px rgba(102, 126, 234, 0.6)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}